import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import PropTypes from "prop-types";
import { Button, Grid } from "@material-ui/core";
import CircularProgresss from "views/components/CircularProgress/CircularProgress";
import ToastModal from "views/components/ToastModal";
import ColumnGroupingTable from "components/UpdateUi/Table/Table";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";
import { toCamelCase } from "utils/Utils";
import { formatAmount } from "utils/Utils";
import { formatTimestamp } from "utils/Utils";
import { saveUserLeadId } from "store/reducers/login/LoginSlice";
import { parseAndSetFlag, parseDateMDY } from "../../../utils/Utils";
import { dashboardTableColumns } from "assets/properties/properties";

const Dashboard = ({ classes }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const outletData = useOutletContext();
  const { authToken } = useSelector((state) => state.login.authData);
  const { mobileNumber } = useSelector((state) => state.login.userData);
  const [selectedTableData, setSelectedTableData] = useState([]);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");
  const [callInProgress, setCallInProgress] = useState(false);
  const [userEligibility,setUserEligibility] = useState(false);
  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };

  useEffect(() => {
      getUserLoanDetails();
      checkUserEligibility();
  }, []);
  const checkUserEligibility = async () => {
     setCallInProgress(true);
    const result = await apiHandler({
      url: endpoint.CHECK_USER_LEAD_ELIGIBILITY,
      method:"GET",
      authToken: authToken,
    });
    setCallInProgress(false);
    if (result?.data?.error_code) {
      toastUpdate("Error", result?.data?.user_description, true);
    } else {
      setUserEligibility(result?.data?.eligible);
    }
  }
  const getUserLoanDetails = async () => {
    setCallInProgress(true);
    const result = await apiHandler({
      url: endpoint.FIND_USER_LOAN_DETAILS_API + mobileNumber,
      authToken: authToken,
    });
    setCallInProgress(false);
    if (result?.data?.error_code === 401) {
      toastUpdate("Error", result?.data?.user_description, true);
    } else {
      console.log(result?.data, "API DATA");
      parseIndicatorData(result?.data);
    }
  };
  const parseIndicatorData = (data) => {
    let reportData = [];
    data = data.filter((item) => item.status !== "COMPLETED");
    data = data?.sort((a, b) => (a?.id < b?.id ? 1 : -1));
    data &&
      data?.forEach((leadDetails, index) => {
        let row = {
          id: index + 1,
          leadId: leadDetails?.id,
          created: leadDetails?.createdBy,
          createdOn: parseDateMDY(leadDetails?.createdOn),
          accountNumber: leadDetails?.loanAccountNumber,
          customerid: leadDetails?.customerId,
          maxEligibleLoanAmount: formatAmount(
            leadDetails?.maxEligibleLoanAmount
          ),
          status: toCamelCase(leadDetails?.status),
          action: (toCamelCase(leadDetails?.status) !== "COMPLETED" && toCamelCase(leadDetails?.status) !== "REJECTED") ? getIcons(leadDetails) : null
        };
        reportData.push(row);
      });
    setSelectedTableData(reportData);
  };
  const getIcons = (leadDetails) => {
  return (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onRowClick(leadDetails);
      }}
    >
      Proceed
    </Button>
  );
};
  const onRowClick = async (data) => {
    const selectedLead = data?.leadId ? data?.leadId : data?.id
    dispatch(
      saveUserLeadId({
        leadId: selectedLead,
        leadStatus: data?.status ? data?.status : "",
      })
    );
    if(data?.status?.toLowerCase() === "rejected"){
      navigate("/loanRejected");
      return;
    }
    setCallInProgress(true);
  
    const result = await apiHandler({
      url: endpoint.USER_PROFILE_SCREEN_FLAGS_API + selectedLead, // Fixed: `leadId` was undefined
      method: "GET",
      authToken: authToken,
    });
  
    setCallInProgress(false);
  
    const flags = result?.data;
    // Check flags and navigate accordingly
    if (!flags.applicantIncomeDetail) {
      navigate("/journey/IncomeDetails", { state: { id: selectedLead} });
      return;
    }
    const cibilResult = await apiHandler({
      url: endpoint.GET_CUSTOMER_CIBIL + selectedLead,
      method: "GET",
      authToken: authToken,
    });

    if (cibilResult?.data?.error_code) {
      navigate("/journey/AadharDetails", {
        state: { id: selectedLead},
      });
      return;
    }
    if (!flags.applicantBankAnalyser || (!flags.bre && flags.applicantBankAnalyser)) {
      navigate("/journey/BankAnalyser", {state: { id: selectedLead, breSuccess: flags.bre },});
    } if (!flags.applicantPhotoKyc) {
      navigate("/journey/SelfiePhoto", { state: { id: selectedLead} });
      return;
    } if (!flags.applicantDetail) {
      navigate("/journey/GeneralDetails", { state: { id: selectedLead} });
      return;
    } if (!flags.applicantLeadReferal) {
      navigate("/journey/ReferenceDetails", { state: { id: selectedLead} });
      return;
    }if (!flags.loanTenure) {
      navigate("/journey/LoanTenureDetails", { state: { id: selectedLead} });
      return;
    }if (!flags.repaymentScheduleDetail) {
      navigate("/journey/RepaymentScheduleDetails", { state: { id: selectedLead} });
      return;
    } if (!flags.applicantBankDetail) {
      navigate("/journey/BankDetails", { state: { id: selectedLead} });
      return;
    } if (!flags.sanctionLetter) {
      navigate("/journey/SanctionLetter", { state: { id: selectedLead} });
      return;
    } if (!flags.sanctionLetterEstamp && parseAndSetFlag(outletData.eStampFlag)) {
      navigate("/journey/EstampSanctionLetter", { state: { id: selectedLead} });
      return;
    } if (!flags.sanctionLetterEsign) {
      navigate("/journey/SanctionLetter", { state: { id: selectedLead} });
      return;
    } if (!flags.mandateDetail) {
      navigate("/journey/MandateDetails", { state: { id: selectedLead} });
      return;
    } if (!flags.disbursal) {
      navigate("/journey/DisbursalDetails", { state: { id: selectedLead} });
      return;
    } if (flags.disbursal){
      // fallback navigation if all steps are complete
      navigate("/completedDetails", { state: { id: selectedLead} });
      return;
    }
  };

  const CreateApplication = async () => {
    dispatch(
      saveUserLeadId({
        leadId: "",
        leadStatus: "",
      })
    );
    navigate("/journey/IncomeDetails", {
      state: { newLeadCreate: false },
    });
  };
  return (
    <Grid container>
      <Grid item xs={12} sm={12} md={12}>
        <SubHeader
          label="Applications"
          button1={userEligibility ? "Add New" : ""}
          button1click={CreateApplication}
          noButton
        />
      </Grid>

      <Grid item xs={12} sm={12} md={12}>
        <ColumnGroupingTable
          columns={dashboardTableColumns}
          rows={selectedTableData}
          onRowClick={onRowClick}
        />
      </Grid>
      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}
      {callInProgress && <CircularProgresss callInProgress={callInProgress} />}
    </Grid>
  );
};

Dashboard.propTypes = {
  classes: PropTypes.object,
  setNext: PropTypes.func,
  setBack: PropTypes.func,
};

export default Dashboard;
